import { Avatar } from 'components/shared/avatar'
import { TextToTextarea } from 'controls/inputs/text_to_textarea'
import React, { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  createPublicContact,
  updatePublicContact as updatePublicContactAction,
} from 'reduxes/slices/public_contacts/actions'

import { css } from 'design'
import { Stack } from 'primitives/containers'

import { useAxios } from '../../hooks/use_axios'
import { useGetPublicContact } from '../../hooks/use_get_public_contact'
import {
  getCurrentHandle,
  getCurrentTeammateHandle,
} from '../../reduxes/slices/current/selectors'

export type ProfileDetailsProps = {
  qualifier: string
} & React.ComponentProps<typeof Stack> &
  React.HTMLAttributes<HTMLDivElement>

export const ProfileDetails = ({
  qualifier,
  ...props
}: ProfileDetailsProps) => {
  const currentHandle = useSelector(getCurrentHandle)
  const currentTeammateHandle = useSelector(getCurrentTeammateHandle)
  const publicContact = useGetPublicContact(currentHandle)
  const dispatch = useDispatch()
  const axios = useAxios()

  const formRef = useRef<HTMLFormElement>(null)

  const formDataImagePrefix = `${qualifier}__image`
  const formDataNamePrefix = `${qualifier}__name`

  const updatePublicContact = useCallback(
    (publicContactData) => {
      if (publicContact) {
        return dispatch(
          axios(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updatePublicContactAction(publicContact.id, publicContactData),
          ),
        )
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return dispatch(axios(createPublicContact(publicContactData)))
      }
    },
    [axios, dispatch, publicContact],
  )

  const updateName = useCallback(() => {
    if (!formRef.current) {
      return Promise.reject()
    }

    const formData = new FormData(formRef.current)
    const publicContactData = new FormData()

    const name = formData.get(formDataNamePrefix)
    if (name && name !== publicContact?.name) {
      publicContactData.append(`public_contact[name]`, name)
    } else {
      return Promise.resolve()
    }

    updatePublicContact(publicContactData)
  }, [formDataNamePrefix, publicContact?.name, updatePublicContact])

  const updateImage = useCallback(
    (event) => {
      event?.preventDefault()
      if (!formRef.current) {
        return Promise.reject()
      }

      const formData = new FormData(formRef.current)
      const publicContactData = new FormData()

      const image = formData.get(formDataImagePrefix)
      if (image) {
        publicContactData.append(`public_contact[image]`, image)
      } else {
        return Promise.resolve()
      }

      updatePublicContact(publicContactData)
    },
    [formDataImagePrefix, updatePublicContact],
  )

  return (
    <Stack
      gap="4"
      css={{
        width: '100%',
      }}
      {...props}
    >
      <form ref={formRef}>
        <div className={'topic__main__menu__header__avatar'}>
          <input
            className="topic__main__menu__header__avatar__inputfile"
            id={formDataImagePrefix}
            type="file"
            name={formDataImagePrefix}
            accept="image/gif, image/jpeg, image/png"
            onChange={updateImage}
          />
          <label
            className={'topic__main__menu__header__avatar__imagelabel'}
            htmlFor={formDataImagePrefix}
          >
            <Avatar handle={currentTeammateHandle} size={53} />
            <div
              className={'topic__main__menu__header__avatar__imagelabel__text'}
            >
              Change Profile Image
            </div>
          </label>
        </div>
        <h3 className="topic__main__menu__heading">Public Name</h3>
        <TextToTextarea
          name={formDataNamePrefix}
          actualValue={publicContact?.name || ''}
          placeholder={'Add Public Name'}
          onBlur={updateName}
          keyCodesToBlurOn={['Enter']}
          buttonProps={{
            className: textAreaStyles(),
          }}
          textareaProps={{
            className: textAreaStyles(),
          }}
        />
      </form>
    </Stack>
  )
}

const textAreaStyles = css({
  width: 'calc(100% + 2px)',
  lineHeight: '24px',
})
